'use client';

import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { type BasicShopUIPropTypes } from 'app/shop-ui/types';
import { type UseIndicatorReturn } from 'shop-ui/tools/Indicator';
import {
    ELEMENT_TYPE,
    LAYOUT_SECTION_NAME,
    FEATURE_FLAG_KEYS,
} from 'app/shop-ui/constants';
import { MediaBackground } from 'shop-ui/component/MediaBackground';
import { BackgroundMask } from 'shop-ui/component/BackgroundMask';
import { columnCountMap } from './rules';
import { GridEditButton } from './GridEditButton';
import './Grid.scss';

interface GridProps extends BasicShopUIPropTypes {
    nextUpdateGridStyle: ReturnType<UseIndicatorReturn['actions']['getResizedStyleMap']>;
    nextUpdateCellStyle: ReturnType<UseIndicatorReturn['actions']['getResizedStyleMap']>;
    sectionName: LAYOUT_SECTION_NAME;
}

function getGridCellCount(className: string = '') {
    const variant = className.split('--')[1];

    if (!variant || !columnCountMap[variant]) {
        return 0;
    }

    return columnCountMap[variant] as number;
}

export const Grid = forwardRef<HTMLDivElement, GridProps>((props, ref) => {
    const {
        children,
        id,
        type,
        style,
        className,
        sectionName,
        styleMap,
        nextUpdateGridStyle,
        nextUpdateCellStyle,
        editingElementId,
        variation,
        backgroundImageURL,
        defaultBackgroundImageURL,
        backgroundVideoURL,
        backgroundMaskColor,
        isResizing,
        enable,
        isEditingMode,
    } = props;

    const childrenList = React.Children.toArray(children);

    const cellCount = getGridCellCount(className);

    if (!styleMap || !id) {
        return null;
    }

    if (!isEditingMode && !enable) {
        return null;
    }

    return (
        <div
            key={`${id}-${variation}`}
            ref={ref}
            style={{
                ...style,
                ...nextUpdateGridStyle,
                ...nextUpdateCellStyle,
            }}
            className={clsx(
                type,
                sectionName !== LAYOUT_SECTION_NAME.HEADER && className,
                !enable && type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL && 'grid-hidden',
                sectionName === LAYOUT_SECTION_NAME.HEADER && 'header-grid',
                styleMap?.[FEATURE_FLAG_KEYS.HEADER_LAYOUT] || ''
            )}
            data-grid-layout-type={variation || undefined}
            {...(isEditingMode &&
                type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL && {
                    'data-shop-element-id': id,
                })}
        >
            {/* 等 image-set 普及後可考慮替換掉圖片背景，https://developer.mozilla.org/en-US/docs/Web/CSS/image/image-set */}
            {(backgroundImageURL || backgroundVideoURL || defaultBackgroundImageURL) && (
                // @ts-ignore
                <MediaBackground
                    type={type}
                    defaultSource={defaultBackgroundImageURL}
                    source={backgroundImageURL || backgroundVideoURL}
                    {...((backgroundImageURL || defaultBackgroundImageURL) && {
                        mediaType: 'image',
                    })}
                    {...(backgroundVideoURL && {
                        mediaType: 'video',
                    })}
                />
            )}
            {backgroundMaskColor && <BackgroundMask mask={backgroundMaskColor} />}
            {Array(cellCount)
                .fill(null)
                .map((_, idx) => (
                    <div
                        key={`${id}-${variation}-cell-${idx}`}
                        className={clsx(`grid-cell-${idx + 1}`, {
                            'grid-cell-editMode': isEditingMode,
                        })}
                        data-shop-component-grid-cell
                    >
                        {childrenList?.[idx] || null}
                        {isEditingMode &&
                            !isResizing &&
                            editingElementId === id &&
                            sectionName !== LAYOUT_SECTION_NAME.HEADER &&
                            type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL && (
                                <GridEditButton
                                    // @ts-ignore
                                    id={childrenList?.[idx]?.props?.children?.props?.id}
                                />
                            )}
                        {isEditingMode &&
                            !isResizing &&
                            editingElementId === id &&
                            sectionName !== LAYOUT_SECTION_NAME.HEADER &&
                            type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL && (
                                <GridEditButton
                                    // @ts-ignore
                                    id={childrenList?.[idx]?.props?.children?.props?.id}
                                />
                            )}
                    </div>
                ))}
        </div>
    );
});

Grid.displayName = 'Grid';
